/**
 * Return material that is at a station.
 * Scan a container label (a sheet of labels is one kind of container), or pick the station and SKU when there is no label.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { fetchInventoryBox, fetchInventoryBoxPutawayContext, type InventoryBox } from "@/lib/inventoryBoxApi";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import type { WarehouseRecountShelfSublocation } from "@/lib/warehouseRecountApi";
import { parseWarehouseRecountScan } from "./warehouseRecountScan";
import {
  WarehouseRecountShelfField,
  warehouseRecountShelfDraftToStorageKey,
  warehouseRecountShelfStorageKeyToDraft,
} from "./WarehouseRecountShelfField";

const RETURN_HOVER_HINT =
  "Scan a container that is at a station, including a sheet of labels, and enter how many are still on it. "
  + "That container goes back on the shelf. Quantity that does not come back leaves on-hand. "
  + "When there is no container label, choose the station and the SKU, then enter the quantity coming back.";

type StationMaterialOption = {
  station_id: number;
  station_name: string;
  location_id: number | null;
  location_name: string | null;
  sku: string;
  qty_on_hand: number;
  description: string | null;
};

type StationReturnResult = {
  station_name: string;
  sku: string;
  quantity_returned: number;
  qty_on_hand: number;
};

export function WarehouseReturnMaterialsPage(): JSX.Element {
  const { showToast } = useHubToast();
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [scanDraft, setScanDraft] = useState("");
  const [sheet, setSheet] = useState<InventoryBox | null>(null);
  const [quantityDraft, setQuantityDraft] = useState("");
  const [shelfDraft, setShelfDraft] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [stationId, setStationId] = useState("");
  const [stationSearch, setStationSearch] = useState("");
  const [sku, setSku] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [looseQuantityDraft, setLooseQuantityDraft] = useState("");
  const [looseShelfDraft, setLooseShelfDraft] = useState("");
  const [looseError, setLooseError] = useState<string | null>(null);
  const [loosePending, setLoosePending] = useState(false);

  const loadSheet = useCallback(async (raw: string) => {
    const parsed = parseWarehouseRecountScan(raw);
    if (parsed == null || parsed.kind !== "box") {
      setScanError("Scan the container label.");
      return;
    }
    setPending(true);
    setScanError(null);
    try {
      const box = await fetchInventoryBox(parsed.hex);
      setSheet(box);
      setQuantityDraft("");
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(box.sublocation));
      setScanDraft("");
    } catch (error) {
      setSheet(null);
      setScanError(error instanceof ApiError ? error.message : "That container was not found.");
    } finally {
      setPending(false);
    }
  }, []);

  useFloorPageScanCapture({
    inputRef: scanRef,
    autoFocus: sheet == null,
    onScan: (value) => {
      void loadSheet(value);
    },
  });

  const shelvesQuery = useQuery({
    queryKey: ["warehouse-return-shelves", sheet?.hex, sheet?.location_id],
    enabled: sheet != null && sheet.location_id != null && sheet.location_id > 0,
    queryFn: () => fetchInventoryBoxPutawayContext(sheet?.hex ?? "", sheet?.location_id ?? 0, "shelves"),
  });

  const optionsQuery = useQuery({
    queryKey: ["warehouse-return-station-materials"],
    queryFn: () => apiFetch<{ rows: StationMaterialOption[] }>("/api/warehouse/return-materials/options"),
  });

  const stationRows = optionsQuery.data?.rows ?? [];
  const stationOptions = useMemo((): SearchableSelectOption[] => {
    const seen = new Set<string>();
    const options: SearchableSelectOption[] = [];
    for (const row of stationRows) {
      const value = String(row.station_id);
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({
        value,
        label: row.station_name,
        description: row.location_name,
      });
    }
    return options;
  }, [stationRows]);

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return stationRows
      .filter((row) => String(row.station_id) === stationId)
      .map((row) => ({
        value: row.sku,
        label: row.sku,
        description: row.description != null && row.description.trim() !== ""
          ? `${row.description} · ${String(row.qty_on_hand)} at the station`
          : `${String(row.qty_on_hand)} at the station`,
      }));
  }, [stationRows, stationId]);

  const selectedLoose = stationRows.find(
    (row) => String(row.station_id) === stationId && row.sku === sku,
  ) ?? null;

  const looseShelvesQuery = useQuery({
    queryKey: ["warehouse-return-loose-shelves", selectedLoose?.location_id, selectedLoose?.sku],
    enabled: selectedLoose != null && selectedLoose.location_id != null && selectedLoose.location_id > 0,
    queryFn: () => apiFetch<{ shelf_sublocations: WarehouseRecountShelfSublocation[] }>(
      `/api/warehouse/return-materials/shelves?sku=${encodeURIComponent(selectedLoose?.sku ?? "")}&hub_location_id=${String(selectedLoose?.location_id ?? 0)}`,
    ),
  });

  const quantityRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (sheet == null) return;
    quantityRef.current?.focus();
  }, [sheet]);
  const returnedQuantity = Number.parseInt(quantityDraft.trim(), 10);
  const quantityValid =
    sheet != null
    && Number.isInteger(returnedQuantity)
    && returnedQuantity > 0
    && returnedQuantity <= sheet.quantity;

  const looseQuantity = Number.parseInt(looseQuantityDraft.trim(), 10);
  const looseQuantityValid =
    selectedLoose != null
    && Number.isInteger(looseQuantity)
    && looseQuantity > 0
    && looseQuantity <= selectedLoose.qty_on_hand;

  const submitReturn = async () => {
    if (sheet == null || !quantityValid || pending) return;
    setPending(true);
    setScanError(null);
    try {
      const result = await apiFetch<{ box: InventoryBox }>("/api/warehouse/return-materials", {
        method: "POST",
        body: {
          box_hex: sheet.hex,
          quantity: returnedQuantity,
          sublocation: warehouseRecountShelfDraftToStorageKey(shelfDraft),
        },
      });
      showToast({
        tone: "success",
        message: `${result.box.short_code} is back with ${String(result.box.quantity)}.`,
      });
      setSheet(null);
      setQuantityDraft("");
      setShelfDraft("");
      scanRef.current?.focus();
    } catch (error) {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not return that container.",
      });
    } finally {
      setPending(false);
    }
  };

  const submitLooseReturn = async () => {
    if (selectedLoose == null || !looseQuantityValid || loosePending) return;
    setLoosePending(true);
    setLooseError(null);
    try {
      const result = await apiFetch<StationReturnResult>("/api/warehouse/return-materials", {
        method: "POST",
        body: {
          station_id: selectedLoose.station_id,
          sku: selectedLoose.sku,
          quantity: looseQuantity,
          sublocation: warehouseRecountShelfDraftToStorageKey(looseShelfDraft),
        },
      });
      showToast({
        tone: "success",
        message: `${result.sku} is back from ${result.station_name}. ${String(result.qty_on_hand)} left at the station.`,
      });
      setSku("");
      setSkuSearch("");
      setLooseQuantityDraft("");
      setLooseShelfDraft("");
      void optionsQuery.refetch();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Could not return that material.";
      setLooseError(message);
      showToast({ tone: "error", message });
    } finally {
      setLoosePending(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={RETURN_HOVER_HINT} breadcrumbLabel="Return materials">
        Return materials
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Container label</CardTitle>
        </CardHeader>
        <CardBody>
          <Label htmlFor="warehouse-return-scan">Container label</Label>
          <Input
            id="warehouse-return-scan"
            ref={scanRef}
            className="mt-1 text-base"
            value={scanDraft}
            autoComplete="off"
            placeholder="Scan the container label"
            onChange={(event) => setScanDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void loadSheet(scanDraft);
            }}
          />
          {scanError != null ? (
            <p role="alert" className="mt-2 flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" />
              {scanError}
            </p>
          ) : null}
        </CardBody>
      </Card>

      {sheet != null ? (
        <Card>
          <CardHeader>
            <CardTitle>Quantity coming back</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void submitReturn();
              }}
            >
              <p className="font-mono text-lg font-semibold text-slate-900">{sheet.short_code}</p>
              <p className="font-mono text-sm text-slate-800">{sheet.sku}</p>
              {sheet.mv_description.trim() !== "" ? (
                <p className="text-sm text-slate-600">{sheet.mv_description}</p>
              ) : null}
              {sheet.floor_station_name != null && sheet.floor_station_name.trim() !== "" ? (
                <p className="text-sm text-slate-700">At {sheet.floor_station_name}.</p>
              ) : null}
              <p className="text-sm text-slate-700">
                {sheet.archived_at != null
                  ? `This container was picked with ${String(sheet.quantity)} on it.`
                  : `This container has ${String(sheet.quantity)} on it.`}
              </p>
              <WarehouseRecountShelfField
                id="warehouse-return-shelf"
                shelfDraft={shelfDraft}
                shelfSublocations={shelvesQuery.data?.shelf_sublocations ?? []}
                disabled={pending}
                onShelfDraftChange={setShelfDraft}
                onCommit={setShelfDraft}
              />
              <div>
                <Label htmlFor="warehouse-return-quantity">Quantity coming back</Label>
                <Input
                  id="warehouse-return-quantity"
                  ref={quantityRef}
                  className="mt-1 text-base"
                  inputMode="numeric"
                  autoComplete="off"
                  value={quantityDraft}
                  onChange={(event) => setQuantityDraft(event.target.value)}
                />
              </div>
              <Button type="submit" disabled={!quantityValid || pending}>
                Return to shelf
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>No container label</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submitLooseReturn();
            }}
          >
            {optionsQuery.error != null ? (
              <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                {optionsQuery.error instanceof ApiError
                  ? optionsQuery.error.message
                  : "Could not load station material."}
              </p>
            ) : null}
            <div>
              <Label htmlFor="warehouse-return-station">Station</Label>
              <SearchableSelect
                id="warehouse-return-station"
                presentation="panelSearch"
                className="mt-1 text-base"
                value={stationId}
                options={stationOptions}
                searchValue={stationSearch}
                onSearchChange={setStationSearch}
                placeholder="Choose the station"
                searchPlaceholder="Station"
                emptyMessage={optionsQuery.isFetching ? "Loading stations…" : "No station has material to return."}
                disabled={loosePending}
                onValueChange={(option) => {
                  setStationId(option?.value ?? "");
                  setStationSearch("");
                  setSku("");
                  setSkuSearch("");
                  setLooseQuantityDraft("");
                  setLooseShelfDraft("");
                }}
              />
            </div>
            <div>
              <Label htmlFor="warehouse-return-sku">SKU</Label>
              <SearchableSelect
                id="warehouse-return-sku"
                presentation="panelSearch"
                className="mt-1 text-base"
                value={sku}
                options={skuOptions}
                searchValue={skuSearch}
                onSearchChange={setSkuSearch}
                placeholder={stationId === "" ? "Choose a station first" : "Choose the SKU"}
                searchPlaceholder="SKU"
                emptyMessage={stationId === "" ? "Choose a station first." : "That station has no SKU on hand."}
                disabled={loosePending || stationId === ""}
                onValueChange={(option) => {
                  setSku(option?.value ?? "");
                  setSkuSearch("");
                  setLooseQuantityDraft("");
                }}
              />
            </div>
            {selectedLoose != null ? (
              <p className="text-sm text-slate-700">
                {String(selectedLoose.qty_on_hand)} on hand at {selectedLoose.station_name}.
              </p>
            ) : null}
            <WarehouseRecountShelfField
              id="warehouse-return-loose-shelf"
              shelfDraft={looseShelfDraft}
              shelfSublocations={looseShelvesQuery.data?.shelf_sublocations ?? []}
              disabled={loosePending || selectedLoose == null}
              onShelfDraftChange={setLooseShelfDraft}
              onCommit={setLooseShelfDraft}
            />
            <div>
              <Label htmlFor="warehouse-return-loose-quantity">Quantity coming back</Label>
              <Input
                id="warehouse-return-loose-quantity"
                className="mt-1 text-base"
                inputMode="numeric"
                autoComplete="off"
                value={looseQuantityDraft}
                disabled={loosePending || selectedLoose == null}
                onChange={(event) => setLooseQuantityDraft(event.target.value)}
              />
            </div>
            {looseError != null ? (
              <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                {looseError}
              </p>
            ) : null}
            <Button type="submit" disabled={!looseQuantityValid || loosePending}>
              Return to shelf
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
